import { useState } from 'react';
import type { JobSuggestion } from '../shared/desktop-api';
import { isJobAdjustment } from '../renderer/learning-news';

/**
 * The count of pending job adjustments on the workspace menu (WorkbenchNavigation), until the person opens them.
 * CapturePoint removed its copy with the old Learning board (its 64a79af3, "dead learning code"); Paige's menu
 * still shows the count, so the hook is Paige's now. The key keeps the stored name, so what was seen stays seen.
 */
const BELL_SEEN_KEY = 'capturepoint:seen-improvements:v1';

const readSeen = (): Record<string, string[]> => {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(BELL_SEEN_KEY) ?? '{}');
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value)
        .slice(-32)
        .map(([key, ids]) => [
          key,
          Array.isArray(ids)
            ? ids.filter((id): id is string => typeof id === 'string').slice(-512)
            : [],
        ]),
    );
  } catch {
    return {};
  }
};

/** A bell acknowledges suggestion identities, not acceptance or changing support counts. */
export function useImprovementBell(jobId: string, suggestions: readonly JobSuggestion[]) {
  const [seen, setSeen] = useState<Record<string, string[]>>(readSeen);
  const pending = suggestions
    .filter((item) => isJobAdjustment(item) && item.status === 'pending')
    .map((item) => item.id);
  const hasUnseen = pending.some((id) => !(seen[jobId] ?? []).includes(id));
  const acknowledge = () =>
    setSeen((previous) => {
      const ids = [...new Set([...(previous[jobId] ?? []), ...pending])].slice(-512);
      const next = Object.fromEntries(
        [...Object.entries(previous).filter(([key]) => key !== jobId), [jobId, ids]].slice(-32),
      );
      try {
        localStorage.setItem(BELL_SEEN_KEY, JSON.stringify(next));
      } catch {
        /* Session state still clears the bell. */
      }
      return next;
    });
  return { hasUnseen, acknowledge };
}
